import { useContext, useMemo } from 'react'
import { StatsigContext } from '@statsig/react-bindings'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { anonymousIdSelector } from 'selectors/segment'
/*
  This hook is used to get the experiment value from Statsig and apply overrides from the query params.

  It can be used to temporarily override the values of an experiment for testing, demonstration, or debugging purposes.
  In every environment, it will return the experiment value based on the anonymousId and the experiment name.
  If we do not have a Statsig provider, it will return default values.

  The query params should be in the format of <experimentName>_<key>=<value>
  The group name should be in the format of <experimentName>_group_name=<groupName>

  true/false values are converted to boolean, everything else is a string.

  Example:
  growth_personalized_booking_confirmation_personalize=true -> { value: { personalize: true } }
  growth_personalized_booking_confirmation_group_name=variant -> { groupName: 'variant' }
*/

const convertBooleanString = (val) => {
  if (val === 'true') return true
  if (val === 'false') return false
  return val
}

export const useStatsigExperiment = (experimentName, defaultGroup = 'control') => {
  const anonymousId = useSelector(anonymousIdSelector)
  const context = useContext(StatsigContext)
  const location = useLocation() // Use useLocation to get the current location

  // Memoize the parsed query parameters
  const experimentOverrides = useMemo(() => {
    const queryParams = new URLSearchParams(location.search)
    const overrides = {}

    queryParams.forEach((value, key) => {
      if (key.startsWith(`${experimentName}_`) && key !== `${experimentName}_group_name`) { // Filter out the group name
        const experimentKey = key.slice(experimentName.length + 1) // Remove 'experimentName_' prefix
        overrides[experimentKey] = convertBooleanString(value)
      }
    })

    return overrides
  }, [location.search, experimentName]) // Dependencies: location.search and experimentName

  const defaultValue = { value: {} }

  let result = defaultValue

  if (anonymousId && context && context.client) {
    result = context.client.getExperiment(experimentName) ?? defaultValue
  }

  result.value = {
    ...result.value,
    ...experimentOverrides,
  }

  const groupParam = new URLSearchParams(location.search).get(`${experimentName}_group_name`)

  result.groupName = groupParam || result.groupName || defaultGroup

  return result
}
